import { put, call, select } from 'redux-saga/effects';
import { isMobile } from 'react-device-detect';
import { Types } from './actions';
import ScheduleRepository from '../../../repositories/ScheduleRepository';
import ScheduleDataRepository from '../../../../data/repositories/ScheduleDataRepository';
import PeriodRepository from '../../../repositories/PeriodRepository';
import PeriodDataRepository from '../../../../data/repositories/PeriodDataRepository';
import ActivitiesRepository from '../../../repositories/ActivitiesRepository';
import ActivitiesDataRepository from '../../../../data/repositories/ActivitiesDataRepository';
import ReinforcementDataRepository from '../../../../data/repositories/ReinforcementDataRepository';
import ReinforcementRepository from '../../../repositories/ReinforcementRepository';
import DateHelpers from '../../../../core/helpers/DateHelper';
import IDHelpers from '../../../../core/helpers/IDHelper';
import constants from '../../../../core/constants';
import AcademicDatesRepository from '../../../repositories/AcademicDatesRepository';
import AcademicDatesDataRepository from '../../../../data/repositories/AcademicDatesDataRepository';
import ProfessorRepository from '../../../repositories/ProfessorRepository';
import ProfessorDataRepository from '../../../../data/repositories/ProfessorDataRepository';

export function findModule(dateToCompare: number, modules: any[]) {
  return modules.filter((value: any) =>
    DateHelpers.isBetweenDates(
      value.startDate + constants.MIL_PERU,
      value.endDate + constants.MIL_PERU,
      dateToCompare
    )
  );
}

export function findWeek(dateToCompare: number, weeks: any[]) {
  const weekFound = weeks.find((value: any) =>
    DateHelpers.isBetweenDates(value.start, value.end, dateToCompare)
  );
  return weekFound
    ? {
        weekId: weekFound.id,
        name: weekFound.desc,
        start: weekFound.start,
        end: weekFound.end
      }
    : { weekId: 0, name: '', start: '', end: '' };
}

export function findVirtualMeetingsByWeek(
  meetings: any[],
  startDate: number,
  academic: any
): any {
  let virtualMeetingsByWeek: any[] = [];
  const initialDay = DateHelpers.getFirstDayOfWeekFromTimestamp(
    startDate + constants.MIL_PERU
  );
  const daysOfWeek = [0, 1, 2, 3, 4, 5, 6].map((day) =>
    DateHelpers.addDays(initialDay, day)
  );
  daysOfWeek.forEach((date: Date) => {
    const meetingsByDay = meetings.filter(
      (meet: any) =>
        DateHelpers.formatDate(meet.start) ===
          DateHelpers.formatDate(date.getTime()) &&
        ((!academic.isCGT && meet.instructionMode === 'VT') ||
          (academic.isCGT &&
            (meet.classType === constants.CLASS_TYPE.virtualPractical ||
              meet.classType === constants.CLASS_TYPE.virtualTheoretical)))
    );
    virtualMeetingsByWeek = virtualMeetingsByWeek.concat(meetingsByDay);
  });
  const grouped = virtualMeetingsByWeek.reduce((r: any, x: any) => {
    const id = x.id.split('_')[0];
    const r1 = r;
    r1[id] = [
      [...(r1[id] || []), x].reduce((a) => ({
        ...a
      }))
    ];
    return r1;
  }, {});
  let accumulateVirtualMeetings: any[] = [];
  Object.keys(grouped).forEach((key: any) => {
    accumulateVirtualMeetings = accumulateVirtualMeetings.concat(grouped[key]);
  });
  return accumulateVirtualMeetings;
}

export function findGlobalMeetingsByWeek(meetings: any[], dates: Date[]): any {
  return dates.map((date) => {
    const meetingsByDay = meetings
      ?.filter(
        (meet: any) =>
          DateHelpers.formatDate(meet?.start) ===
          DateHelpers.formatDate(date.getTime())
      )
      .sort((a: any, b: any) => a?.start - b?.start);

    const fix = meetingsByDay.map((event: any, index: any) => ({
      ...event,
      order: index
    }));

    const fix2 = fix.map((event: any) => ({
      ...event,
      meetings: fix
    }));

    return {
      date,
      meetings: fix2
    };
  });
}

export function getAcademicCalendar(academicsDatesResponse: any): any {
  const today = DateHelpers.getPeruDate();
  today.setHours(5, 0, 0, 0);

  const academicDates: any[] = academicsDatesResponse;
  const nacademicDates = academicDates?.map((date: any) => ({
    ...date,
    startTime: new Date(date.startDate),
    endTime: new Date(date.endDate),
    startDate: date.startDate + constants.MIL_PERU * 2,
    endDate: date.endDate + constants.MIL_PERU * 2
  }));
  const todayTimeStamp = DateHelpers.converDateToEpoch(today) * 1000;
  const academicDatesForToday = nacademicDates.filter((m) =>
    DateHelpers.dateRangeOverlapsAcademic(
      m.startDate,
      m.endDate,
      todayTimeStamp
    )
  );
  const academicDatesForNextDays = nacademicDates.filter(
    (m) => new Date(m.startDate) > today && new Date(m.endDate) > today
  );
  return {
    academicCalendar: academicDatesForToday.concat(academicDatesForNextDays),
    forToday: academicDatesForToday,
    forComingNext: academicDatesForNextDays
  };
}

export function findAcademicCalendar(
  dates: Date[],
  academicCalendar: any[],
  selectedDate: Date
) {
  const newSelectedDate = new Date(selectedDate.getTime());
  newSelectedDate.setHours(0, 0, 0, 0);
  const clone = new Date(newSelectedDate.getTime() + constants.MIL_PERU);
  const datesNew = isMobile
    ? [clone]
    : dates.map((d: Date) => new Date(d.setHours(5)));
  const startDate = datesNew[0];
  const endDate = datesNew[datesNew.length - 1];

  const calendarByWeek: any[] = [];
  academicCalendar.forEach((f) => {
    const start = new Date(f.startDate);
    const end = new Date(f.endDate);
    let el = null;
    if (start < startDate && startDate <= end && endDate >= end) {
      el = { ...f, startDate, left: true, endDate: end };
    } else if (end > endDate && start >= startDate && start <= endDate) {
      el = { ...f, startDate: start, right: true, endDate };
    } else if (start >= startDate && end <= endDate) {
      el = { ...f, startDate: start, endDate: end };
    } else if (start < startDate && end > endDate) {
      el = { ...f, startDate, left: true, endDate, right: true };
    }
    if (el) calendarByWeek.push(el);
  });
  const rangesByWeek = datesNew.map((d) => {
    const rangesByDay = calendarByWeek.filter(
      (f: any) =>
        (f.endDate <= d && f.startDate >= d) ||
        (d >= f.startDate && d <= f.endDate)
    );
    return { day: d, rangesByDay };
  });

  const ids = [];
  const elements: any[] = [];
  const rangesByWeekOrder = rangesByWeek.map((r) => ({
    day: r.day,
    count: r.rangesByDay.length,
    firstId: r.rangesByDay[0]?.id,
    firstText: r.rangesByDay[0]?.name,
    rangesByDay: r.rangesByDay.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    )
  }));

  rangesByWeekOrder.forEach((item) => {
    const order = item.rangesByDay.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );
    const prev = item.day.getDay() === 0 ? 7 : item.day.getDay();
    elements.push({
      isHidden: true,
      day: prev,
      id: item.firstId,
      count: item.count,
      size: 0,
      start: prev,
      text: item.firstText
    });
    order.forEach((range, index) => {
      if (range.startDate.getTime() === item.day.getTime()) {
        const size =
          1 +
          (range.endDate.getTime() - range.startDate.getTime()) /
            (24 * 60 * 60 * 1000);
        ids.push(range.id);
        elements.push({
          id: range.id,
          overlaping: index,
          size,
          start: prev,
          left: range.left || false,
          right: range.right || false,
          text: range.title
        });
      }
    });
  });
  return elements;
}

export default function* execute(action: any): any {
  try {
    const startHour = 7;
    const endHour = 23;
    const availableDays = [1, 2, 3, 4, 5, 6, 7];
    const today = DateHelpers.getPeruDate();
    const initialMonth = DateHelpers.getMonthName(Date.now());
    const initialYear = DateHelpers.getYearWithFormat(Date.now());
    const firstDayOfWeek = DateHelpers.getFirstDayOfWeek();
    const dates = availableDays.map((numberDay: number) =>
      DateHelpers.addDays(firstDayOfWeek, numberDay - 1)
    );

    // find  academic calendar;
    const academicDatesRepository: AcademicDatesRepository =
      new AcademicDatesDataRepository();

    const { data: dataPeriods } = yield select(
      (state: any) => state.getPeriods
    );
    const currentPeriod = dataPeriods?.find((p: any) => !!p.isCurrent);

    const academicDatesResponse = yield call(
      academicDatesRepository.getAcademicDates,
      currentPeriod?.period ?? ''
    );
    const calendar = getAcademicCalendar(academicDatesResponse);
    const academicCalendarByWeek = findAcademicCalendar(
      dates,
      calendar.academicCalendar,
      today
    );
    let dataHeader: any = {
      academicCalendar: calendar.academicCalendar,
      academicCalendarByWeek,
      academicCalendarList: {
        data: {
          forToday: calendar?.forToday,
          forComingNext: calendar?.forComingNext
        }
      }
    };
    yield put({ type: Types.GET_SCHEDULE_REQUEST, data: dataHeader });
    // consultar el periodo actual de UTP
    const periodRepository: PeriodRepository = new PeriodDataRepository();
    const activitiesRepository: ActivitiesRepository =
      new ActivitiesDataRepository();
    const reinforcementRepository: ReinforcementRepository =
      new ReinforcementDataRepository();
    const professorRepository: ProfessorRepository =
      new ProfessorDataRepository();
    const periodResponse = yield call(
      periodRepository.getCurrentPeriod,
      currentPeriod?.period ?? ''
    );
    const professorResponse = yield call(professorRepository.getProfessors);
    if (!periodResponse) {
      yield put({
        type: Types.GET_SCHEDULE_FAILURE,
        error: 'Ocurrió un error al leer el periodo actual.'
      });
      return;
    }
    const {
      weeks,
      modules,
      id,
      description,
      shortDescription,
      startDate,
      endDate
    } = periodResponse;

    const startDateFilter: number = (startDate || 0) - 1000 * 60 * 60 * 24 * 7;
    const weekRow = findWeek(today.getTime(), weeks);
    const firstDateOfWeek = DateHelpers.formatMonthDate(
      weekRow.start + constants.MIL_PERU
    );

    const lastDateOfWeek = DateHelpers.formatMonthDate(
      weekRow.end + constants.MIL_PERU
    );

    const { weekId } = weekRow;
    const sch = dates.map((d) => ({ date: d, meetings: [], activities: [] }));
    const currentModules = findModule(today.getTime(), modules);

    dataHeader = {
      dataHeader,
      startHour,
      endHour,
      today,
      dates,
      firstDateOfWeek,
      lastDateOfWeek,
      schedule: sch,
      initialMonth,
      initialYear,
      weekId,
      firstDayOfWeek,
      weeks,
      modules,
      currentModule: currentModules,
      period: { id, description, shortDescription, startDate, endDate },
      selectedDate: today
    };
    yield put({ type: Types.GET_SCHEDULE_REQUEST, data: dataHeader });

    // consultar horario
    const scheduleRepository: ScheduleRepository = new ScheduleDataRepository();
    const scheduleResponse = yield call(
      scheduleRepository.getSchedule,
      startDateFilter, // action.startDate,
      action.endDate
    );

    if (!scheduleResponse) {
      yield put({
        type: Types.GET_SCHEDULE_FAILURE,
        error: 'Ocurrió un error al leer el horario.'
      });
      return;
    }

    // consultar eventos
    const activitiesResponse = yield call(activitiesRepository.getActivities);

    // consultar reforzamiento
    const reinforcementResponse = yield call(
      reinforcementRepository.getReinforcements
    );
    // consultar examenes
    // const examsResponse = yield call(examsRepository.getExams);

    const meetings: any[] = scheduleResponse.schedule.items;
    const activities: any[] = activitiesResponse?.subscriptions;
    const reinforcements: any[] = reinforcementResponse?.subscribed || [];
    const isCGTMode = yield select(
      (state: any) => state.profile?.data?.academic.isCGT
    ) || null;
    const meetingsWithProfessor = meetings.map((m: any) => ({
      ...m,
      eventId: IDHelpers.generateID(),
      professor: professorResponse.filter(
        (prof: any) => prof.courseId === m.id.split('_')[0]
      )
    }));
    const nactivitiesF = activities?.map((a: any) => {
      const arrayDate = a.date.split('-');
      const stdate = `${arrayDate[0]}/${arrayDate[1]}/${arrayDate[2]}`;
      return {
        start: new Date(`${stdate} ${a.startTime}`).getTime(),
        end: new Date(`${stdate} ${a.endTime}`).getTime(),
        date: new Date(`${stdate}`).getTime() + constants.MIL_PERU,
        professor: '',
        name: a.service.name,
        reservationCode: a.code,
        idZoom: a.link || '',
        type: 'A',
        eventId: IDHelpers.generateID()
      };
    });
    const nreinforcementsF = reinforcements.map((r: any) => {
      const arrayDate = r.date.split('-');
      const stdate = `${arrayDate[0]}/${arrayDate[1]}/${arrayDate[2]}`;
      return {
        start: new Date(`${stdate} ${r.startTime}`).getTime(),
        end: new Date(`${stdate} ${r.endTime}`).getTime(),
        date: new Date(stdate).getTime(),
        professor: r.professor,
        name: r.description,
        idZoom: r.link || '',
        type: 'R',
        eventId: IDHelpers.generateID()
      };
    });
    // filter only remote courses
    let nmeetingsF = meetingsWithProfessor.filter(
      (m) =>
        m.instructionMode === 'P' ||
        (m.instructionMode === 'SP' &&
          [
            constants.CLASS_TYPE.faceToFaceTheoretical,
            constants.CLASS_TYPE.faceToFacePractical
          ].includes(m.classType))
    );
    nmeetingsF = nmeetingsF.map((m: any) => ({
      name: m.name,
      start: m.start,
      end: m.end,
      professor: m.professor,
      type: 'C',
      idZoom: '',
      isReprog: m.isReprog,
      eventId: m.eventId,
      instructionMode: m.instructionMode,
      classType: m.classType,
      linkZoom: m.linkZoom
    }));
    const AllEvents = nmeetingsF.concat(nactivitiesF).concat(nreinforcementsF);
    const schedule = findGlobalMeetingsByWeek(AllEvents, dates);
    // filter only virtual courses
    const virtualMeetingsAll = meetingsWithProfessor.filter(
      (meet: any) =>
        (meet.instructionMode === 'SP' &&
          isCGTMode &&
          [
            constants.CLASS_TYPE.virtualPractical,
            constants.CLASS_TYPE.virtualTheoretical
          ].includes(meet.classType)) ||
        meet.instructionMode === 'VT' ||
        [
          constants.CLASS_TYPE.virtualPractical,
          constants.CLASS_TYPE.virtualTheoretical
        ].includes(meet.classType)
    );
    const academic = yield select(
      (state: any) => state.profile?.data?.academic
    );
    const virtualMeetingsByWeek = findVirtualMeetingsByWeek(
      meetingsWithProfessor,
      dates[0].getTime(),
      academic
    );

    const selectedDate = DateHelpers.getPeruDate();
    selectedDate.setHours(0, 0, 0, 0);
    const data = {
      ...dataHeader,
      schedule,
      virtualMeetings: virtualMeetingsByWeek,
      startHour,
      endHour,
      today,
      dates,
      weekId: weekRow.weekId,
      firstDateOfWeek,
      lastDateOfWeek,
      firstDayOfWeek,
      weeks,
      selectedDate,
      academicCalendarByWeek,
      virtualMeetingsAll,
      meetingsGlobal: AllEvents,
      availableDays,
      academicCalendar: calendar.academicCalendar,
      academicCalendarList: {
        data: {
          forToday: calendar?.forToday,
          forComingNext: calendar?.forComingNext
        }
      }
    };
    yield put({ type: Types.GET_SCHEDULE_SUCCESS, data });
  } catch (error) {
    yield put({
      type: Types.GET_SCHEDULE_FAILURE,
      error: 'Error al leer horarios'
    });
    throw error;
  }
}
